import { Injectable, signal } from '@angular/core';
import type { CombinationPriceCell } from '@models/combination-prices';
import type { CellPrice } from '../pricing-grid/pricing-grid.model';
import { AbstractPriceGridStore, gridKey, parseGridKey } from '../pricing-grid/pricing-grid.store';
import type { CombinationCoords, GridAxis, GridBounds } from './combination-pricing.model';

/**
 * Owns the in-memory cell map for {@link CombinationPricingComponent}: the full price grid across all
 * seasons × stay types × party compositions. Every mutation is memory-only — the parent form's Save
 * persists the grid via the component's `save()`. Provided at the component level so each pricing section
 * gets its own isolated grid state.
 *
 * All cell state and math live in {@link AbstractPriceGridStore}, shared with the exclusive whole-lake
 * grid. What is specific here is the anglers × non-anglers axes: the store also holds the unit's
 * {@link bounds}, because they decide which cells may exist (pruning on a shrink) and which may be saved.
 */
@Injectable()
export class CombinationPricingGridStore extends AbstractPriceGridStore<CombinationCoords, CombinationPriceCell> {
  private readonly boundsState = signal<GridBounds>({ minAnglers: 1, maxAnglers: null, maxNonAnglers: null });

  /** The grid's current capacity bounds; the component derives its rendered axes from these. */
  readonly bounds = this.boundsState.asReadonly();

  /**
   * Mirror the unit's minimum-anglers capacity, normalised on the way in to the lowest row the grid can
   * render: no minimum (null, 0, or a nonsense negative) means "start at one angler". Normalising here
   * rather than at each reader keeps the rule in one place — {@link bounds} consumers use it as given.
   */
  setMinAnglers(value: number | null): void {
    this.boundsState.update((bounds) => ({ ...bounds, minAnglers: value && value > 0 ? value : 1 }));
  }

  setMax(axis: GridAxis, value: number | null): void {
    const field = axis === 'anglers' ? 'maxAnglers' : 'maxNonAnglers';
    this.boundsState.update((bounds) => ({ ...bounds, [field]: value }));
  }

  load(cells: CombinationPriceCell[]): void {
    this.replaceCells(toEntries(cells));
  }

  /** Keys of filled cells whose `axis` coordinate would exceed the proposed new maximum. */
  filledCellsBeyond(axis: GridAxis, newMax: number): string[] {
    const keys: string[] = [];
    for (const [key, cell] of this.snapshot()) {
      if (cell.purchasePrice == null && cell.sellingPrice == null) continue;
      const [, , anglers, nonAnglers] = parseGridKey(key);
      const coord = axis === 'anglers' ? anglers : nonAnglers;
      if (coord > newMax) keys.push(key);
    }
    return keys;
  }

  /** Drop the given cells after a confirmed axis shrink (leaves the error flag untouched). */
  prune(keys: string[]): void {
    this.removeCells(keys);
  }

  /** Keys under the given season × stay type, and whether any of them hold a price. */
  visibleKeys(seasonId: number, stayTypeId: number): { keys: string[]; hasData: boolean } {
    const prefix = `${seasonId}:${stayTypeId}:`;
    const cells = this.snapshot();
    const keys = [...cells.keys()].filter((k) => k.startsWith(prefix));
    const hasData = keys.some((k) => {
      const cell = cells.get(k);
      return cell != null && (cell.purchasePrice != null || cell.sellingPrice != null);
    });
    return { keys, hasData };
  }

  /** Clear the given (visible) keys; also resets the error flag as the grid changed under the user. */
  clear(keys: string[]): void {
    this.removeCells(keys);
    this.showErrors.set(false);
  }

  /**
   * Replace the whole grid with the copied cells that fit the current capacity; out-of-range cells
   * would never render or save, so they are dropped. Returns copied/skipped counts for the toast.
   */
  replaceFitting(cells: CombinationPriceCell[]): { copied: number; skipped: number } {
    const fitting = this.withinBounds(cells);
    this.replaceCells(toEntries(fitting), true);
    return { copied: fitting.length, skipped: cells.length - fitting.length };
  }

  /** Flatten to the save payload: only fully-priced cells within the current bounds. */
  toPayload(): CombinationPriceCell[] {
    const cells = this.filledCells().map(
      ({ coords: [periodTypeId, periodTimeId, anglers, nonAnglers], purchasePrice, sellingPrice }) => ({
        periodTypeId,
        periodTimeId,
        anglers,
        nonAnglers,
        purchasePrice,
        sellingPrice,
      }),
    );
    return this.withinBounds(cells);
  }

  private withinBounds(cells: CombinationPriceCell[]): CombinationPriceCell[] {
    const { minAnglers, maxAnglers, maxNonAnglers } = this.bounds();
    // minAnglers is already the grid's rendered lower row bound. Without this filter, cells below that row
    // stay hidden yet are still persisted, and can never be edited or removed.
    return cells.filter(
      (c) =>
        c.anglers >= minAnglers &&
        (maxAnglers == null || c.anglers <= maxAnglers) &&
        (maxNonAnglers == null || c.nonAnglers <= maxNonAnglers),
    );
  }
}

function toEntries(cells: CombinationPriceCell[]): (readonly [string, CellPrice])[] {
  return cells.map(
    (c) =>
      [
        gridKey([c.periodTypeId, c.periodTimeId, c.anglers, c.nonAnglers]),
        { purchasePrice: c.purchasePrice, sellingPrice: c.sellingPrice },
      ] as const,
  );
}
